import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { installAudioUnlock, startAlarm } from "../utils/alertSound";
import { MdSupportAgent, MdBolt, MdClose } from "react-icons/md";
import { GuestServiceRequestDto } from "../utils/guestServiceRequestsApi";

interface NewRoomServiceRequestPopupProps {
  request: GuestServiceRequestDto;
  /** Resolved client-side from request.buildingId against the account's
   * buildings — the request DTO only carries the id. */
  buildingName?: string | null;
  onDismiss: () => void;
  onView: () => void;
}

/** Center-screen modal for a just-arrived guest-service request (Amenities/
 * House/Help/Travel — never Food, which has its own popup in
 * RestaurantPage/NewRestaurantOrderPopup). Mirrors NewRestaurantOrderPopup's
 * design exactly for a consistent feel across both queues. Dismissing just
 * closes it (the request stays "new" for the sidebar badge); "View Request"
 * is what marks it seen. */
const NewRoomServiceRequestPopup: React.FC<NewRoomServiceRequestPopupProps> = ({
  request,
  buildingName,
  onDismiss,
  onView,
}) => {
  const navigate = useNavigate();

  // Rings while the popup is open, so a new request is heard as well as seen.
  useEffect(() => {
    installAudioUnlock();
    return startAlarm();
  }, []);

  const handleView = () => {
    onView();
    navigate("/admin/room-service");
  };

  const isComplaint = request.requestType === "Complaint";

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 animate-overlay-fade-in"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-modal-pop-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-teal-100 text-teal-700 text-2xl">
              <MdSupportAgent />
              <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-teal-500" />
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                New Room Service Request
                {request.isUrgent && (
                  <span className="ml-2 inline-flex items-center gap-0.5 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700 align-middle">
                    <MdBolt /> Urgent
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500">
                {request.requestNumber ?? `#${request.requestId}`}
                {request.requestedOn
                  ? ` · ${new Date(request.requestedOn).toLocaleString()}`
                  : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            className="shrink-0 text-xl text-gray-400 hover:text-gray-600"
          >
            <MdClose />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Guest</p>
              <p className="font-medium text-gray-900">
                {request.guestName ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Category</p>
              <p className="font-medium text-gray-900">
                {request.categoryName ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Room Type</p>
              <p className="font-medium text-gray-900">
                {request.roomTypeName ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Building</p>
              <p className="font-medium text-gray-900">
                {buildingName ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Room</p>
              <p className="font-medium text-gray-900">
                {request.roomNo ?? "—"}
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              {isComplaint ? "Complaint" : "Items"}
            </p>
            {isComplaint ? (
              <p className="text-sm text-gray-800">
                {request.complaintText || "—"}
              </p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {(request.items ?? []).map((item) => (
                  <div
                    key={item.requestDetailId}
                    className="flex items-center justify-between text-sm text-gray-800"
                  >
                    <span>
                      {item.itemName} × {item.quantity}
                    </span>
                    {item.sectionName && (
                      <span className="text-xs text-gray-400">
                        {item.sectionName}
                      </span>
                    )}
                  </div>
                ))}
                {(request.items ?? []).length === 0 && (
                  <p className="text-sm text-gray-400">—</p>
                )}
              </div>
            )}
          </div>

          {request.notes && (
            <div className="text-sm">
              <p className="text-gray-500">Notes for staff</p>
              <p className="text-gray-900">{request.notes}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onDismiss}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Dismiss
          </button>
          <button
            onClick={handleView}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          >
            View Request
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewRoomServiceRequestPopup;
